import * as React from 'react';
import { ModuleIcon } from './module-icon';
import css from './module-launcher.module.css';

const s = css as any;

/** Below this many modules the list is short enough to read, so there is no search box. */
const SEARCH_FROM = 8;
/** Below this many modules a dock only repeats the grid, so it is not shown. */
const DOCK_FROM = 8;
/** The dock holds as many of the first modules as fit on ONE row of the drawer — never fewer
 *  than a phone's four, never more than ten, and never more than half the modules, so the grid
 *  below it is not the smaller part. */
const DOCK_MIN = 4;
const DOCK_MAX = 10;
/** One dock slot: the tile (`.ModuleDock .ModuleLauncherTile`) plus the gap beside it. */
const DOCK_SLOT_PX = 120;
/** The dock's own left + right padding and border (`.ModuleDock`). */
const DOCK_INSET_PX = 34;

const dockSizeFor = (width: number, moduleCount: number): number => {
  const fits = Math.floor((width - DOCK_INSET_PX) / DOCK_SLOT_PX);
  return Math.max(DOCK_MIN, Math.min(fits, DOCK_MAX, Math.floor(moduleCount / 2)));
};

/** One module as the launcher shows it. Each app maps its own module model onto this. */
export interface ILauncherModule {
  key: React.Key;
  /** The name under the icon, already translated. Search matches it. */
  label: string;
  href: string;
  /** Font Awesome class. */
  icon?: string;
  /** A ready glyph (e.g. an antd icon) for apps without Font Awesome; drawn instead of `icon`. */
  glyph?: React.ReactNode;
  /** One line on what the module is for; shown as the tile's tooltip. */
  description?: string;
  /** Stored hex color; clamped so white reads on it. */
  color?: string;
  /** An all-capitals acronym here (CCM, RPM) is drawn as letters instead of the icon. */
  code?: string;
  /** The module the user is in: bold label and a dot underneath. */
  current?: boolean;
}

interface IModuleLauncherProps {
  /** In the order the user should see them; the first ones go in the dock. */
  modules: ILauncherModule[];
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  emptyText: string;
  onOpen: (module: ILauncherModule, evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

/**
 * The Modules drawer's body: a search box, the first modules in a dock that fills the drawer's
 * width, and an app-icon grid of the rest (the phone home-screen layout). While searching there
 * is no dock — just the matches.
 */
export const ModuleLauncher: React.FC<IModuleLauncherProps> = ({
  modules, search, onSearchChange, searchPlaceholder, emptyText, onOpen,
}) => {
  const query = ( search || '' ).trim().toLowerCase();
  const matches = query ? modules.filter(m => ( m.label || '' ).toLowerCase().includes(query)) : modules;

  // The dock's size follows the drawer's width, so measure the row it sits in.
  const rowRef = React.useRef<HTMLDivElement>(null);
  const [rowWidth, setRowWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    setRowWidth(row.clientWidth);
    const observer = new ResizeObserver(([entry]) => setRowWidth(entry.contentRect.width));
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  const dock = !query && modules.length >= DOCK_FROM
    ? modules.slice(0, dockSizeFor(rowWidth, modules.length))
    : [];
  const grid = matches.slice(dock.length);

  const tile = (m: ILauncherModule) => (
    <a
      key={m.key}
      href={m.href}
      className={`${s.ModuleLauncherTile} ${m.current ? s.ModuleLauncherTileCurrent : ''}`}
      aria-current={m.current ? 'page' : undefined}
      title={m.description}
      aria-label={m.description ? m.label : undefined}
      onClick={evt => onOpen(m, evt)}
    >
      <ModuleIcon icon={m.icon} glyph={m.glyph} color={m.color} code={m.code}/>
      <span className={s.ModuleLauncherLabel}>{m.label}</span>
    </a>
  );

  return (
    <>
      {modules.length >= SEARCH_FROM && (
        <input
          className={s.LauncherSearch}
          placeholder={searchPlaceholder}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          autoFocus
        />
      )}
      <div ref={rowRef} className={s.ModuleDockRow}>
        {dock.length > 0 && <div className={s.ModuleDock}>{dock.map(tile)}</div>}
      </div>
      <div className={s.ModuleLauncherGrid}>
        {grid.map(tile)}
        {matches.length === 0 && <div className={s.ModuleLauncherEmpty}>{emptyText}</div>}
      </div>
    </>
  );
};
